import React, { useRef, useEffect, useState } from 'react';
import { snapshot_ChartHeaderButtons as ChartHeaderButtons } from './snapshot_ChartHeaderButtons';
import useResizeObserver from '@/hooks/useResizeObserver';
import { useSnapshotRow } from '../contexts/SnapshotRowContext';
import { useShareUrl } from '@/hooks/useShareUrl';
import { useDownloadWithAuth } from '@/hooks/useDownloadWithAuth';
import { downloadChartSection, downloadAllChartsWithWatermark, downloadChartWithCanvasWatermark } from '@/utils/chartDownload';
import { createChartId } from '@/utils/shareUtils';
import { snapshotMetricDefs } from '../data/SnapshotAboutTheDataDefs';

interface MetricCardProps {
  title: string;
  description: string;
  changePercent: string;
  changeDescription: string;
  changeType: 'positive' | 'negative';
  value: string;
  valueUnit: string;
  valueUnitDescription?: string;
  className?: string;
  rowIndex: number;
  cardIndex: number;
  chartName?: string;
  shareLink?: string;
}

export const snapshot_MetricCard: React.FC<MetricCardProps> = ({
  title,
  description,
  changePercent,
  changeDescription,
  changeType,
  value,
  valueUnit,
  valueUnitDescription,
  className = "",
  rowIndex,
  cardIndex,
  chartName,
  shareLink
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { rowHeights, headerHeights, updateHeight, updateHeaderHeight, removeCard } = useSnapshotRow();
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { height: contentHeight } = useResizeObserver(contentRef);
  
  // Use provided shareLink or fall back to current URL
  const currentShareLink = useShareUrl();
  const finalShareLink = shareLink || currentShareLink;
  const finalChartName = chartName || title;
  const metricDef = snapshotMetricDefs[finalChartName as keyof typeof snapshotMetricDefs];

  // --- Download state and refs ---
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const { executeWithAuth } = useDownloadWithAuth();

  // Single-card download - using canvas watermark to avoid DOM layout interference
  const downloadSingleCard = async () => {
    console.log('[MetricCard] Starting download for:', finalChartName);
    console.log('[MetricCard] Chart ref:', chartRef.current);
    
    if (!chartRef.current) {
      console.error('[MetricCard] No chart ref found for:', finalChartName);
      return;
    }

    setIsDownloading(true);
    try {
      console.log('[MetricCard] Calling downloadChartWithCanvasWatermark for:', finalChartName);
      // Use canvas-based watermark to avoid DOM layout interference with our positioning fixes
      // The improved chart download now preserves calculated positions for chart elements
      await downloadChartWithCanvasWatermark(chartRef.current, `${finalChartName.toLowerCase().replace(/\s+/g, '-')}`, {
        watermarkOptions: {
          opacity: 0.5, 
          size: { width: 200, height: 60 }
        },
        includeTimestamp: false,
        disableWatermark: true, // Disable watermark for individual cards
      });
      console.log('[MetricCard] Download completed successfully for:', finalChartName);
    } catch (err) {
      console.error('[MetricCard] Download error for', finalChartName, ':', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Download entire snapshot section as one canvas image
  const downloadSectionAsImage = async () => {
    console.log('[downloadSectionAsImage] Capturing full snapshot section');
    setIsDownloading(true);
    try {
      const section = document.getElementById('snapshot-charts-section');
      if (!section) {
        console.warn('Snapshot charts section not found');
        return;
      }
      // Use the improved chart download that preserves calculated positions for chart elements
      await downloadChartSection(section, 'snapshot-charts');
    } catch (error) {
      console.error('[downloadSectionAsImage] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadOption = async (option: 'single' | 'all') => {
    setIsDownloadPopupOpen(false);
    if (option === 'single') {
      await executeWithAuth(downloadSingleCard, 'single');
    } else {
      await executeWithAuth(downloadSectionAsImage, 'all'); // Back to section-as-one-canvas
    }
  };

  const handleDownloadButtonClick = () => {
    if (isDownloading) return;
    setIsDownloadPopupOpen(true);
  };
  
  useEffect(() => {
    updateHeight(rowIndex, cardIndex, headerHeight);
  }, [updateHeight, rowIndex, cardIndex, headerHeight]);

  useEffect(() => {
    updateHeaderHeight(rowIndex, cardIndex, contentHeight);
  }, [updateHeaderHeight, rowIndex, cardIndex, contentHeight]);

  useEffect(() => {
    // Cleanup function
    return () => {
      removeCard(rowIndex, cardIndex);
    };
  }, [removeCard, rowIndex, cardIndex]);

  // Get the maximum heights for this row
  const rowMaxHeight = rowHeights.get(rowIndex) || 0;
  const headerMaxHeight = headerHeights.get(rowIndex) || 0;

  const tickerStyles = changeType === 'positive' 
    ? "text-[#4B7400] bg-[#F3FAE5]" 
    : "text-[#B30000] bg-[#FFDADA]";

  return (
    <article 
      ref={chartRef} 
      id={createChartId(finalChartName, 'sn')}
      className={`bg-white w-full min-w-[280px] rounded-[20px] relative flex flex-col h-full ${className}`}
    >
      <header 
        ref={headerRef} 
        className="w-full px-6 relative pt-6 pb-6 max-md:max-w-full max-md:px-5 flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:max-md:inset-x-5 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full"
        style={{ 
          minHeight: `${rowMaxHeight}px`,
          transition: 'min-height 0.2s ease-in-out'
        }}
      >
        <div 
          ref={contentRef}
          className="w-full"
          style={{
            minHeight: `${headerMaxHeight}px`,
            transition: 'min-height 0.2s ease-in-out'
          }}
        >
          <div className="flex flex-row-reverse flex-wrap items-start justify-between gap-x-6 gap-y-4 mb-3">
            <ChartHeaderButtons
              chartName={finalChartName}
              shareLink={finalShareLink}
              metricDefinition={metricDef}
              aboutDescription={description}
              isDownloading={isDownloading}
              isDownloadPopupOpen={isDownloadPopupOpen}
              onDownloadClick={handleDownloadButtonClick}
              onDownloadOption={handleDownloadOption}
              onDownloadPopupClose={() => setIsDownloadPopupOpen(false)}
            />
            <h2 className="text-[#212121] text-[28px] font-semibold leading-tight align-middle flex-1 chart-text-shift min-w-[200px]" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
              {title}
            </h2>
          </div>
          <p className="text-[#212121] text-xl font-normal leading-tight align-middle mt-3 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {description}. Brought to you by Govstream.ai.
          </p>
        </div>
      </header>
      <div className="flex w-full flex-col items-stretch px-6 pb-4 flex-grow justify-end max-md:px-5">
        <div className="flex items-center gap-2 text-sm font-medium leading-tight align-middle mt-20 chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
          <div className={`justify-center items-center self-stretch flex my-auto px-3 py-1.5 rounded-[20px] ${tickerStyles}`}>
            <span className="flex items-center gap-1">
              {changePercent !== '0.0%' && (
                <span 
                  className={`material-symbols-outlined text-sm transition-transform duration-200 ticker-arrow-icon ${changeType === 'negative' ? 'rotate-180' : ''}`}
                  style={{ fontSize: '16px' }}
                >
                  arrow_upward_alt
                </span>
              )}
              <span className="self-stretch my-auto text-sm">
                {changePercent.startsWith('-') ? changePercent.slice(1) : changePercent}
              </span>
            </span>
          </div>
          <span className="text-[#212121] self-stretch my-auto opacity-70 leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
            {changeDescription}
          </span>
        </div>
        <div className="grid w-full mt-2 gap-x-2.5" style={{gridTemplateColumns: 'max-content auto', alignItems: 'end'}}>
          <span className="text-[#212121] text-[48px] font-bold leading-none max-md:text-[48px] chart-text-shift">
            {value}
          </span>
          <span className="text-[#212121] text-xl font-medium leading-tight opacity-70 chart-text-shift min-w-0" style={{wordBreak: 'break-word'}}>
            {valueUnitDescription || valueUnit}
          </span>
        </div>
      </div>
    </article>
  );
};
